import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      <Header />

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-8 font-['Outfit']">Terms & Conditions</h1>
          <p className="text-slate-600 mb-8">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">1. Introduction</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                These Terms and Conditions ("Terms") govern your use of the QuickFinance.io website and services. By accessing our website or using our services, you agree to be bound by these Terms.
              </p>
              <p className="text-slate-600 leading-relaxed">
                QuickFinance.io is a credit broker, not a lender.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">2. Our Services</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                QuickFinance.io provides a credit broking service that connects businesses with funding partners. Our services include:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Collecting information about your business and funding requirements</li>
                <li>Assessing initial eligibility based on information provided</li>
                <li>Introducing you to suitable funding partners</li>
                <li>Providing support throughout the application process</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                We do not make lending decisions. All funding decisions are made solely by our funding partners based on their own criteria.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">3. Eligibility</h2>
              <p className="text-slate-600 leading-relaxed mb-4">To use our services, you must:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Be at least 18 years of age</li>
                <li>Be a UK resident or operate a UK-based business</li>
                <li>Have authority to apply on behalf of the business (if applicable)</li>
                <li>Provide accurate and truthful information</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">4. Your Obligations</h2>
              <p className="text-slate-600 leading-relaxed mb-4">When using our services, you agree to:</p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Provide accurate, complete, and up-to-date information</li>
                <li>Not provide false or misleading information</li>
                <li>Notify us of any changes to your circumstances</li>
                <li>Use the website and services lawfully</li>
                <li>Not attempt to gain unauthorised access to our systems</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">5. Commission and Fees</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                <strong>Our fee:</strong> We may receive a commission from our funding partners for successful introductions. This does not affect the amount you borrow or repay.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                <strong>Funding partner fees:</strong> Interest rates, arrangement fees, and other charges are set by the funding partner and will be clearly disclosed in any offer you receive.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>No upfront fees:</strong> We do not charge any fees for using our website or applying for funding.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">6. Credit Checks</h2>
              <p className="text-slate-600 leading-relaxed">
                Initial eligibility checks are typically soft searches that do not affect your credit score. However, if you proceed with a full application, our funding partners may conduct a full credit check, which will be recorded on your credit file. You will be informed before any hard credit check is performed.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">7. No Guarantee of Funding</h2>
              <p className="text-slate-600 leading-relaxed">
                We do not guarantee that you will receive funding or a particular funding offer. All decisions are made by our funding partners at their sole discretion. An initial eligibility indication is not a guarantee of approval.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">8. Intellectual Property</h2>
              <p className="text-slate-600 leading-relaxed">
                All content on this website, including text, graphics, logos, and software, is the property of QuickFinance.io or our licensors and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">9. Limitation of Liability</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                To the fullest extent permitted by law:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>We are not liable for any indirect, incidental, or consequential damages</li>
                <li>We are not liable for decisions made by funding partners</li>
                <li>Our total liability is limited to the fees you have paid us (if any)</li>
                <li>We do not guarantee the accuracy of information provided by third parties</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">10. Regulatory Information</h2>
              <p className="text-slate-600 leading-relaxed">
                QuickFinance.io is authorised and regulated by the Financial Conduct Authority (FCA). Our FCA registration number can be verified on the FCA register at register.fca.org.uk. We are a member of a Financial Ombudsman Service approved dispute resolution scheme.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">11. Governing Law</h2>
              <p className="text-slate-600 leading-relaxed">
                These Terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">12. Changes to Terms</h2>
              <p className="text-slate-600 leading-relaxed">
                We may update these Terms from time to time. Changes will be posted on this page with an updated "Last updated" date. Continued use of our services after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-4 font-['Outfit']">13. Contact Us</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="bg-slate-50 rounded-xl p-6">
                <p className="text-slate-700"><strong>QuickFinance.io</strong> — a trading style of Connexis Group Ltd</p>
                <p className="text-slate-600">Email: legal@quickfinance.io</p>
                <p className="text-slate-600">Email: hello@quickfinance.io</p>
                <p className="text-slate-600">Address: 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ</p>
                <p className="text-slate-600">Company No. 17214446 | ICO Registration No. ZC148599</p>
              </div>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
